// Facade over the heavy FIT-conversion module. @garmin/fitsdk (~0.35MB
// decompressed) lives entirely in ./client-convert-impl, which is code-split
// into its own chunk and loaded on demand the first time a user actually
// inspects, converts, or repairs a file. Keeping the SDK out of the
// statically-imported surface means it no longer ships in the initial bundle
// that gates first paint (LCP) on the converter, batch, and repair pages —
// and stops it leaking into unrelated routes (e.g. /pricing) via shared
// vendor chunking.
//
// Only the type surface and the trivial, synchronous `detectSourceFormat`
// live here; importing those pulls in no runtime code, so the common case of
// `import type { TrackPoint } from "@/lib/client-convert"` stays free.

export type {
  TrackPoint,
  StripFields,
  DeviceOverride,
  ActivityEdits,
  TrimWindow,
  FileSummary,
  FileData,
  RepairResult,
} from "./client-convert-impl";

import type { ActivityEdits, FileData, RepairResult } from "./client-convert-impl";
import type { ConversionTarget } from "./formats";

// Cheap filename check — no SDK needed. It runs during render, so it must stay
// synchronous and dependency-free (keeping it here avoids pulling in the impl
// module just to detect a format).
export function detectSourceFormat(file: File): "fit" | "tcx" {
  return file.name.toLowerCase().endsWith(".fit") ? "fit" : "tcx";
}

export async function getFileData(file: File): Promise<FileData> {
  const m = await import("./client-convert-impl");
  return m.getFileData(file);
}

export async function convertFile(
  file: File,
  target: ConversionTarget,
  edits?: ActivityEdits
): Promise<Blob> {
  const m = await import("./client-convert-impl");
  return m.convertFile(file, target, edits);
}

export async function repairFitClient(file: File): Promise<RepairResult> {
  const m = await import("./client-convert-impl");
  return m.repairFitClient(file);
}
